import { DocumentApi } from 'Api/Document/DocumentApi';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { UserDefinedReference } from 'Components/UserDefinedReference/UserDefinedReference';
import { ISSUE_SOURCE, REFERENCE } from 'Config/Tooltips';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { toTitleCase } from 'Helpers/StringUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { IssueHistoryResponse, IssueResponse, IssueStatus } from 'Models/Issues';
import styles from 'Styles/DetailsPage.module.css';

interface IssueDetailsCurrentSnapshotProps {
    documentApi: DocumentApi;
    issueResponse: IssueResponse;
    type: 'current';
}

interface IssueDetailsHistoricalSnapshotProps {
    documentApi: DocumentApi;
    issueHistoryResponse: IssueHistoryResponse;
    type: 'historical';
}

type IssueDetailsSnapshotProps = IssueDetailsCurrentSnapshotProps | IssueDetailsHistoricalSnapshotProps;

/**
 * Renders details for an issue (current or historical), minus the impacted controls or impacted third party.
 * Used in the "main" area of the Issue Details page, and in historical modals on the same page.
 */
export const IssueDetailsSnapshot = (props: IssueDetailsSnapshotProps) => {
    const cachedData = useCachedData();

    const issue = props.type === 'current' ? props.issueResponse : props.issueHistoryResponse.issue;
    const tags = props.type === 'current' ? cachedData.convertTagIdsToDisplayableTags(props.issueResponse.tags) : props.issueHistoryResponse.issue.tags;

    /** Returns a displayable value for a given string, or '-' if the value is empty or undefined. */
    const getDisplayValue = (value?: string): string => {
        if (!value) {
            return '-';
        }
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : '-';
    };

    /** Returns a displayable user name for a given subject, or '-' if the subject is undefined or the user cannot be found. */
    const getUserDisplayName = (subject?: string): string => {
        if (!subject) {
            return '-';
        }
        return getUserNameFromSubject(subject, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? '-';
    };

    /** Returns a title-case display value for a given string, or '-' if the value is empty or undefined. */
    const getTitleCaseDisplayValue = (value?: string, separator?: string): string => {
        const titleCaseValue = toTitleCase(value, separator);
        return titleCaseValue ? titleCaseValue : '-';
    };

    return (
        <div className={styles.detailsContainer}>
            <div className={styles.detailsGridSection}>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Status" />
                        <Text variant="Text2">{getTitleCaseDisplayValue(issue.status)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Owner" />
                        <Text>{getUserDisplayName(issue.owner_subject)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Created" />
                        <Text>{`${iso8601ToUsDateShort(issue.created_timestamp)} by ${getUserNameFromSubject(issue.created_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}`}</Text>
                    </div>
                </div>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Priority" />
                        <Text variant="Text2">{getTitleCaseDisplayValue(issue.priority)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Delegates" />
                        <Text>{issue.delegates.length === 0 ? '-' : issue.delegates.map((delegate) => getUserDisplayName(delegate)).join(', ')}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Source" tooltipText={ISSUE_SOURCE} />
                        <Text>{getDisplayValue(issue.source)}</Text>
                    </div>
                </div>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Due Date" />
                        <Text variant="Text2">{issue.due_date ? iso8601ToUsDateShort(issue.due_date) : '-'}</Text>
                    </div>
                </div>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Reference" tooltipText={REFERENCE} />
                <UserDefinedReference reference={issue.reference} />
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Description" />
                <Text preserveLineBreaks>{getDisplayValue(issue.description)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Risk Description" />
                <Text preserveLineBreaks>{getDisplayValue(issue.risk_assessment)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Remediation Plan" />
                <Text preserveLineBreaks>{getDisplayValue(issue.remediation_plan)}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Files" />
                {issue.files.length === 0 ? <Text>-</Text> : [...issue.files].sort((a, b) => (a.filename > b.filename ? 1 : -1)).map((file) => <UploadedFileAndState key={file.file_id} file={file} documentApi={props.documentApi} />)}
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Closure Statement" />
                <Text preserveLineBreaks>{issue.status === IssueStatus.CLOSED ? getDisplayValue(issue.closure_statement) : '-'}</Text>
            </div>
            <div className={styles.labelAndValueContainer}>
                <FieldLabel fieldName="Tags" />
                {issue.tags.length > 0 ? <TagListing tags={tags} showAll /> : <Text noStyles>-</Text>}
            </div>
        </div>
    );
};
